import { Link } from "react-router-dom";
import { Clock, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { type BlogPost } from "@/data/blog-posts";
import { type Lang } from "@/lib/translations";
import { blogCategories } from "@/data/blog-posts";

interface BlogCardProps {
  post: BlogPost;
  lang: Lang;
}

export default function BlogCard({ post, lang }: BlogCardProps) {
  const categoryLabel = blogCategories.find((c) => c.key === post.category)?.label[lang] ?? post.category;
  const dateStr = new Date(post.publishedAt).toLocaleDateString(lang === "en" ? "en-US" : "es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-card hover:-translate-y-1 transition-all duration-200 flex flex-col"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={post.coverImage.replace(/-(1024|1600)\.webp$/, "-640.webp")}
          decoding="async"
          alt={post.title[lang]}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          width={600}
          height={375}
        />
      </div>
      <div className="p-5 flex flex-col flex-1 gap-3">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs font-semibold">
            {categoryLabel}
          </Badge>
        </div>
        <h3 className="font-heading font-bold text-card-foreground text-lg leading-snug group-hover:text-primary transition-colors">
          {post.title[lang]}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed flex-1">
          {post.excerpt[lang]}
        </p>
        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t border-border">
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {dateStr}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {post.readingTime} min {lang === "en" ? "read" : "lectura"}
          </span>
        </div>
      </div>
    </Link>
  );
}
